import { supabaseConfigured } from '../net/supabase.js';
import { createGame, joinGame, addAi, startGame, loadSeats } from '../net/api.js';
import { subscribeGame } from '../net/sync.js';

// The lobby has three sub-views: the mode picker (create vs join), a
// waiting room that the host + joiners share, and an error state.
// We render the whole lobby content area from one function so state
// transitions are straightforward.

const SEAT_ORDER = ['East', 'South', 'West', 'North'];

let activeUnsub = null;
let lobbyState = {
  mode: 'pick', // pick | create | join | waiting | error
  code: '',
  displayName: '',
  gameId: '',
  mySeat: '',
  isHost: false,
  seats: [],
  error: '',
};
let onGameStartedCallback = null;

export function showLobbyPlaceholder(onGameStarted) {
  onGameStartedCallback = onGameStarted;
  cleanup();
  lobbyState = {
    mode: 'pick',
    code: '',
    displayName: '',
    gameId: '',
    mySeat: '',
    isHost: false,
    seats: [],
    error: '',
  };
  render();
}

function cleanup() {
  if (activeUnsub) {
    activeUnsub();
    activeUnsub = null;
  }
}

function render() {
  const root = document.getElementById('lobbyContent');
  if (!root) return;

  if (!supabaseConfigured()) {
    root.innerHTML = `
      <div class="lobby-box">
        <h2>Play Online</h2>
        <p>Online play requires Supabase credentials. Copy
        <code>.env.local.example</code> to <code>.env.local</code>, add your
        project URL and anon key, and restart the dev server.</p>
      </div>
    `;
    return;
  }

  const s = lobbyState;
  if (s.mode === 'pick') return renderPick(root);
  if (s.mode === 'create') return renderCreate(root);
  if (s.mode === 'join') return renderJoin(root);
  if (s.mode === 'waiting') return renderWaiting(root);
}

function renderPick(root) {
  root.innerHTML = `
    <div class="lobby-box">
      <h2>Play Online</h2>
      <p>Create a table for your friends, or join with a code.</p>
      <button class="btn btn-jade" id="lbCreate" style="display:block;width:100%;margin-bottom:10px">Create a Game</button>
      <button class="btn btn-gold" id="lbJoin" style="display:block;width:100%">Join a Game</button>
    </div>
  `;
  document.getElementById('lbCreate').onclick = () => {
    lobbyState.mode = 'create';
    render();
  };
  document.getElementById('lbJoin').onclick = () => {
    lobbyState.mode = 'join';
    render();
  };
}

function renderCreate(root) {
  const err = lobbyState.error ? `<div class="lobby-error">${lobbyState.error}</div>` : '';
  root.innerHTML = `
    <div class="lobby-box">
      <h2>Create a Game</h2>
      <p>Pick a display name — the other players will see it.</p>
      ${err}
      <input class="lobby-input" id="lbName" placeholder="Your name" maxlength="32" value="${escapeHtml(lobbyState.displayName)}" />
      <div style="display:flex;gap:10px;margin-top:6px">
        <button class="btn btn-outline" id="lbBack" style="flex:1">← Back</button>
        <button class="btn btn-jade" id="lbSubmit" style="flex:2">Create</button>
      </div>
    </div>
  `;
  document.getElementById('lbBack').onclick = () => {
    lobbyState.mode = 'pick';
    lobbyState.error = '';
    render();
  };
  document.getElementById('lbSubmit').onclick = async () => {
    const name = document.getElementById('lbName').value.trim();
    if (!name) return setError('Please enter a name.');
    lobbyState.displayName = name;
    try {
      const { game_id, code, seat } = await createGame(name);
      lobbyState.gameId = game_id;
      lobbyState.code = code;
      lobbyState.mySeat = seat;
      lobbyState.isHost = true;
      lobbyState.mode = 'waiting';
      lobbyState.error = '';
      await refreshSeats();
      subscribe();
      render();
    } catch (e) {
      setError(errorCopy(e.message));
    }
  };
}

function renderJoin(root) {
  const err = lobbyState.error ? `<div class="lobby-error">${lobbyState.error}</div>` : '';
  root.innerHTML = `
    <div class="lobby-box">
      <h2>Join a Game</h2>
      <p>Enter the 6-character code from your host.</p>
      ${err}
      <input class="lobby-input" id="lbCode" placeholder="ABC123" maxlength="6" style="text-transform:uppercase;letter-spacing:6px;text-align:center" value="${escapeHtml(lobbyState.code)}" />
      <input class="lobby-input" id="lbName" placeholder="Your name" maxlength="32" value="${escapeHtml(lobbyState.displayName)}" />
      <div style="display:flex;gap:10px;margin-top:6px">
        <button class="btn btn-outline" id="lbBack" style="flex:1">← Back</button>
        <button class="btn btn-jade" id="lbSubmit" style="flex:2">Join</button>
      </div>
    </div>
  `;
  document.getElementById('lbBack').onclick = () => {
    lobbyState.mode = 'pick';
    lobbyState.error = '';
    render();
  };
  document.getElementById('lbSubmit').onclick = async () => {
    const code = document.getElementById('lbCode').value.trim().toUpperCase();
    const name = document.getElementById('lbName').value.trim();
    if (code.length !== 6) return setError('Code must be 6 characters.');
    if (!name) return setError('Please enter a name.');
    lobbyState.code = code;
    lobbyState.displayName = name;
    try {
      const { game_id, seat } = await joinGame(code, name);
      lobbyState.gameId = game_id;
      lobbyState.mySeat = seat;
      lobbyState.isHost = false;
      lobbyState.mode = 'waiting';
      lobbyState.error = '';
      await refreshSeats();
      subscribe();
      render();
    } catch (e) {
      setError(errorCopy(e.message));
    }
  };
}

function renderWaiting(root) {
  const s = lobbyState;
  const byId = new Map(s.seats.map((row) => [row.seat, row]));
  const rows = SEAT_ORDER.map((seatName) => {
    const row = byId.get(seatName);
    const mine = row && row.seat === s.mySeat;
    if (row) {
      return `
        <div class="seat-row">
          <span class="seat-label">${seatName}</span>
          <span class="seat-name">${escapeHtml(row.display_name)}${mine ? ' (you)' : ''}</span>
          ${row.is_ai ? '<span class="seat-badge">AI</span>' : ''}
        </div>
      `;
    }
    const btn = s.isHost
      ? `<button class="btn btn-outline btn-sm" data-add-seat="${seatName}">Add AI</button>`
      : '<span class="seat-badge">Waiting…</span>';
    return `
      <div class="seat-row">
        <span class="seat-label">${seatName}</span>
        <span class="seat-empty">empty</span>
        ${btn}
      </div>
    `;
  }).join('');

  const canStart = s.isHost && s.seats.length === 4;
  const err = s.error ? `<div class="lobby-error">${s.error}</div>` : '';

  root.innerHTML = `
    <div class="lobby-box">
      <h2>${s.isHost ? 'Your Game' : 'Waiting to Start'}</h2>
      <p>${s.isHost ? 'Share this code with your friends:' : 'Code:'}</p>
      <div class="lobby-code">${s.code}</div>
      ${err}
      <div class="seat-list">${rows}</div>
      ${s.isHost
        ? `<button class="btn btn-jade" id="lbStart" style="width:100%" ${canStart ? '' : 'disabled'}>Start Game</button>`
        : `<p style="color:var(--gray);font-size:.88rem">Waiting for the host to start…</p>`}
      <button class="btn btn-outline" id="lbLeave" style="width:100%;margin-top:10px">Leave</button>
    </div>
  `;

  document.getElementById('lbLeave').onclick = () => {
    cleanup();
    lobbyState.mode = 'pick';
    render();
  };

  if (s.isHost) {
    root.querySelectorAll('[data-add-seat]').forEach((btn) => {
      btn.onclick = async () => {
        try {
          await addAi(s.gameId, btn.dataset.addSeat);
          await refreshSeats();
          render();
        } catch (e) {
          setError(errorCopy(e.message));
          render();
        }
      };
    });
    const startBtn = document.getElementById('lbStart');
    if (startBtn) {
      startBtn.onclick = async () => {
        try {
          await startGame(s.gameId);
          // Realtime update will trigger onGame with status='play';
          // we also proactively call the callback in case the subscription
          // hasn't attached yet on very slow connections.
          if (onGameStartedCallback) onGameStartedCallback(s.gameId, s.mySeat);
          cleanup();
        } catch (e) {
          setError(errorCopy(e.message));
          render();
        }
      };
    }
  }
}

function subscribe() {
  cleanup();
  const { gameId } = lobbyState;
  activeUnsub = subscribeGame(gameId, {
    onGame: (row) => {
      if (row?.status === 'play' || row?.status === 'charleston' || row?.status === 'done') {
        if (onGameStartedCallback) onGameStartedCallback(gameId, lobbyState.mySeat);
        cleanup();
      }
    },
    onSeats: async () => {
      await refreshSeats();
      render();
    },
  });
}

async function refreshSeats() {
  try {
    lobbyState.seats = await loadSeats(lobbyState.gameId);
  } catch (e) {
    setError(errorCopy(e.message));
  }
}

function setError(msg) {
  lobbyState.error = msg;
  render();
}

function errorCopy(code) {
  const map = {
    invalid_code: 'That code doesn\'t look right.',
    game_not_found: 'No game with that code.',
    game_already_started: 'That game has already started.',
    game_full: 'That table is already full.',
    display_name_required: 'Please enter a name.',
    not_host: 'Only the host can do that.',
    need_four_seats: 'All 4 seats need to be filled to start.',
    supabase_not_configured: 'Online play is not configured.',
  };
  return map[code] || 'Something went wrong: ' + code;
}

function escapeHtml(s) {
  return (s ?? '').toString().replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
}
